import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CategoryIcon from '@mui/icons-material/Category';
import { useSearchParams, useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import api from "../../conf/api";
import Scrollbars from "react-custom-scrollbars-2";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import UserPopupBox from "./components/UserPopupBox.js";
import { connect } from "react-redux";
import { authSignup, ghostLogin } from "../../store/actions/actions.js";
import CreateIcon from "@mui/icons-material/Create";
import DeleteIcon from "@mui/icons-material/Delete";
import KeyIcon from '@mui/icons-material/Key';
import LoginIcon from '@mui/icons-material/Login';
import ConfirmDeleteUserBox from "./components/ConfirmDeleteUserBox.js";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PasswordResetBox from "./components/PasswordResetBox.js";
import UserChannelsPreview from "./components/UserChannelsPreview.js";
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox';
import TvIcon from '@mui/icons-material/Tv';
import { toast } from "react-toastify";
import CategoryManagerDialog from "./components/CategoryManagerDialog.jsx";
import GhostLoginConfirmBox from "./components/GhostLoginConfirmBox.js";
import UserFilters, { EMPTY_FILTERS, toQueryParams } from "./components/UserFilters.js";

// The four role tabs. Kept as data so the markup is one <Button> instead of
// four near-identical copies that have to be edited in lockstep.
const VIEW_TABS = [
  { value: "admin", label: "Users View" },
  { value: "creator", label: "Creator View" },
  { value: "promo", label: "Promo View" },
  { value: "creator_client", label: "Creator Clients View" },
];

function StaffList(props) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryScreen = searchParams.get("screen");
  const [view, setView] = useState(queryScreen || "admin");
  const [params, setParams] = useState(null);
  const [data, setData] = useState([]);
  const [userBoxOpen, setUserBoxOpen] = useState(false);
  const [categoriesBoxOpen, setcategoriesBoxOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: parseInt(searchParams.get("page") || 1),
    pageSize: parseInt(searchParams.get("page_size") || 10)
  });
  const [isUpdate, setIsUpdate] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [confirmBoxOpen, setConfirmBoxOpen] = useState(false);
  const [passResetOpen, setPassResetOpen] = useState(false);
  const [channelPreviewOpen, setChannelPreviewOpen] = useState(false);
  const [ghostLoginConfirmOpen, setGhostLoginConfirmOpen] = useState(false);
  // Id of the user whose contract link is currently being signed, so the row
  // can show a spinner instead of letting the click be fired twice.
  const [contractLoadingFor, setContractLoadingFor] = useState(null);
  const [filters, setFilters] = useState({ ...EMPTY_FILTERS });

  const handleUpdateUser = useCallback((id) => {
    setSelectedRow(id);
    setIsUpdate(true);
    setUserBoxOpen(true);
  }, []);

  const handleDeleteUser = useCallback((id) => {
    setSelectedRow(id);
    setConfirmBoxOpen(true);
  }, []);

  const handlePassReset = useCallback((user) => {
    setSelectedRow(user);
    setPassResetOpen(true);
  }, []);

  const handleChannelPreview = useCallback((user) => {
    setSelectedRow(user);
    setChannelPreviewOpen(true);
  }, []);

  const handleGhostLogin = useCallback((user) => {
    setSelectedRow(user);
    setGhostLoginConfirmOpen(true);
  }, []);

  // Contracts live in a PRIVATE S3 bucket, so there is no durable URL to put
  // in the row. Ask the backend for a freshly signed, short-lived link at the
  // moment of the click and open that. Nothing may be appended to the returned
  // URL — an extra query param invalidates the SigV4 signature (S3 answers 403).
  const handleViewContract = useCallback(async (user) => {
    setContractLoadingFor(user.id);
    try {
      const res = await api.get(`/users/contract/${user.id}`);
      if (!res.data?.url) {
        toast.error("No contract is available for this user.");
        return;
      }
      window.open(res.data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.detail || "Could not open the contract. Please try again."
      );
    } finally {
      setContractLoadingFor(null);
    }
  }, []);

  const performGhostLogin = useCallback((userId) => {
    props.ghostLogin(
      userId,
      (user) => {
        // Success callback - redirect to home or dashboard
        navigate("/");
      },
      (error) => {
        // Error is already handled in the action
        console.error("Ghost login failed:", error);
      }
    );
  }, [props, navigate]);

  const handlePaginationChange = useCallback((newModel) => {
    const newPage = newModel.page + 1; // Ensure it's 1-based

    setPaginationModel(prevState => {
      const updatedModel = { ...prevState, ...newModel, page: newPage };

      // Update search params for pagination
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("page", newPage);
      updatedParams.set("page_size", newModel.pageSize);
      setSearchParams(updatedParams);

      return updatedModel;
    });
    fetchData(params, { ...newModel, page: newPage });
  }, [params, searchParams, setSearchParams]);


  // Monotonic id of the newest request. Switching tab and typing a search
  // fire two overlapping requests, and the broader one (more rows) often
  // resolves LAST — which would paint unfiltered rows over the filtered ones
  // while the filter chips still claim the search is applied. Only the newest
  // request is allowed to touch state.
  const requestSeq = useRef(0);

  const fetchData = useCallback(async (fetchParams = params, pagination = paginationModel) => {
    const seq = ++requestSeq.current;
    setLoading(true);

    try {
      // Trailing slash: without it Django's APPEND_SLASH answers 301 and every
      // list request pays a pointless extra round trip.
      const res = await api.get("/users/list/", {
        params: {
          ...fetchParams,
          ...toQueryParams(filters),
          page: pagination.page,
          page_size: pagination.pageSize,
        },
      });
      if (seq !== requestSeq.current) return; // superseded
      setData(res.data.results);
      setRowCount(res.data.count);
    } catch (err) {
      if (seq !== requestSeq.current) return; // superseded
      console.error(err);
      setData([]);
      setRowCount(0);
      // The filter endpoint answers bad input with a 400 and a per-field
      // message. Show it — an empty grid with no explanation looks like a bug.
      const detail = err?.response?.data;
      if (err?.response?.status === 400 && detail && typeof detail === "object") {
        const first = Object.values(detail)[0];
        toast.error(Array.isArray(first) ? first[0] : String(first));
      }
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [params, paginationModel, filters]);

  useEffect(() => {
    fetchData();
  }, [params]);

  // Re-query when a filter changes. Always from page 1: staying on page 7 of a
  // now-narrower result set just shows an empty grid. The ref skips the very
  // first run, which the [params] effect above already covers.
  const skipFirstFilterRun = useRef(true);
  useEffect(() => {
    if (skipFirstFilterRun.current) {
      skipFirstFilterRun.current = false;
      return;
    }
    const firstPage = { ...paginationModel, page: 1 };
    setPaginationModel(firstPage);
    fetchData(params, firstPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const page = parseInt(searchParams.get("page") || 1);
    const pageSize = parseInt(searchParams.get("page_size") || 10);

    setPaginationModel({ page, pageSize });
  }, [searchParams]);

  // Build params based on view and pagination
  useEffect(() => {
    const newParams = {
      is_creator: view === "creator",
      is_promouser: view === "promo",
      is_admin: view === "admin",
      is_creator_client: view === "creator_client",
      // page: paginationModel.page,
      // page_size: paginationModel.pageSize,
    };
    setParams(newParams);
  }, [view]);

  // Sync view state with query params
  useEffect(() => {
    if (queryScreen !== view) {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("screen", view);
      setSearchParams(updatedParams);
    }
  }, [view, queryScreen, searchParams, setSearchParams]);


  const gridStyles = useMemo(() => ({
    border: "none",
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: "grey.100",
      borderBottom: "2px solid",
      borderColor: "grey.300",
    },
    "& .MuiDataGrid-columnHeaderTitle": { fontWeight: 700, color: "grey.700" },
    "& .MuiDataGrid-row:nth-of-type(odd)": { backgroundColor: "grey.50" },
    "& .MuiDataGrid-row:hover": { backgroundColor: "rgba(102, 115, 252, 0.10)" },
    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": { outline: "none" },
    // Declared last, and matched on row+class, so it outranks the zebra
    // striping above — a soft-deleted row must stay visibly red either way.
    "& .MuiDataGrid-row.deleted-row, & .MuiDataGrid-row.deleted-row:hover": {
      backgroundColor: "rgba(211, 47, 47, 0.12)",
    },
  }), []);


  const columns = useMemo(() => [
    { field: "id", headerName: "ID", sortable: true, width: 90 },
    { field: "username", headerName: "User", sortable: true, width: 200 },
    { field: "email", headerName: "Email", sortable: true, minWidth: 200 },
    {
      field: "paypal_email",
      headerName: "Paypal Email",
      sortable: true,
      minWidth: 180,
    },
    { field: "country", headerName: "Country", sortable: true },
    {
      field: "promo_shares",
      headerName: "Promo Revenue (%)",
      type: "number",
      sortable: true,
      minWidth: 150,
    },
    {
      field: "creator_revenew",
      headerName: "Creator Revenue (%)",
      sortable: true,
      minWidth: 160,
    },
    {
      field: "address",
      headerName: "Address",
      sortable: true,
      minWidth: 200,
      flex: 1,
    },
    {
      field: "is_active",
      headerName: "Active",
      sortable: true,
      align: "center",
      width: 90,
      type: "boolean",
    },
    {
      field: "deleted",
      headerName: "Deleted",
      sortable: true,
      width: 90,
      align: "center",
      type: "boolean",
    },
    {
      field: "date_joined",
      headerName: "Created Date",
      sortable: true,
      width: 180,
      type: "dateTime",
      valueGetter: (params) => new Date(params.row?.date_joined),
    },
    {
      field: "action",
      headerName: "Action",
      align: "center",
      headerAlign: "center",
      sortable: false,
      width: 260,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          {props.user?.is_superuser && !params.row.is_superuser && (
            <Tooltip title="Ghost Login - Login as this user">
              <IconButton size="small" onClick={() => handleGhostLogin(params.row)}>
                <LoginIcon fontSize="small" sx={{ color: "warning.main" }} />
              </IconButton>
            </Tooltip>
          )}
          {params.row.is_promouser && (
            <Tooltip title="Channels view">
              <IconButton size="small" onClick={() => handleChannelPreview(params.row)}>
                <TvIcon fontSize="small" color="primary" />
              </IconButton>
            </Tooltip>
          )}
          {params.row.has_contract && (
            <Tooltip title="View Contract">
              <span>
                <IconButton
                  size="small"
                  disabled={contractLoadingFor === params.row.id}
                  onClick={() => handleViewContract(params.row)}
                >
                  {contractLoadingFor === params.row.id ? (
                    <CircularProgress size={16} />
                  ) : (
                    <DescriptionOutlinedIcon fontSize="small" color="primary" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Tooltip title="Edit User">
            <IconButton size="small" onClick={() => handleUpdateUser(params.row.id)}>
              <CreateIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete User">
            <IconButton size="small" onClick={() => handleDeleteUser(params.row.id)}>
              <DeleteIcon fontSize="small" sx={{ color: "error.dark" }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Reset Password">
            <IconButton size="small" onClick={() => handlePassReset(params.row)}>
              <KeyIcon fontSize="small" sx={{ color: "coral" }} />
            </IconButton>
          </Tooltip>
          {!params.row.is_verified && (
            <Tooltip title="Generate New Verification Key">
              <IconButton size="small" onClick={regenerateVerificationLink(params.row)}>
                <ForwardToInboxIcon fontSize="small" sx={{ color: "error.light" }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ], [handleChannelPreview, handleUpdateUser, handleDeleteUser, handlePassReset, handleGhostLogin, handleViewContract, contractLoadingFor, props.user]);

  const regenerateVerificationLink = useCallback((user) => async () => {
    try {
      const res = await api.post(
        "/auth/verification/regen/",
        { email: user.email },
        { timeout: 1000 * 60 * 5 }
      );
      if (res.status === 200) {
        toast.info(res.data);
        fetchData();
      }
    } catch (err) {
      toast.error("Something went wrong.");
    }
  }, [fetchData]);

  const UserDialog = useCallback(() => (
    <UserPopupBox
      selectedRow={selectedRow}
      is_update={isUpdate}
      open={userBoxOpen}
      setOpen={setUserBoxOpen}
      registerUser={props.authSignup}
      refreshFunc={fetchData}
      is_promouser={view === "promo"}
      is_staff={view === "admin"}
      is_creator={view === "creator"}
    />
  ), [selectedRow, isUpdate, userBoxOpen, props.authSignup, fetchData, view]);

  const DeleteUserDialog = useCallback(() => (
    <ConfirmDeleteUserBox
      selectedRow={selectedRow}
      open={confirmBoxOpen}
      setOpen={setConfirmBoxOpen}
      refreshFunc={fetchData}
    />
  ), [confirmBoxOpen, selectedRow, fetchData]);

  const PassResetDialog = useCallback(() => (
    selectedRow &&
    <PasswordResetBox
      selectedRow={selectedRow}
      open={passResetOpen}
      setOpen={setPassResetOpen}
    />
  ), [passResetOpen, selectedRow]);

  const UserChannelsPreviewBox = useCallback(() => (
    selectedRow &&
    <UserChannelsPreview
      selectedRow={selectedRow}
      open={channelPreviewOpen}
      setOpen={setChannelPreviewOpen}
    />
  ), [channelPreviewOpen, selectedRow]);

  const GhostLoginDialog = useCallback(() => (
    selectedRow &&
    <GhostLoginConfirmBox
      open={ghostLoginConfirmOpen}
      setOpen={setGhostLoginConfirmOpen}
      selectedUser={selectedRow}
      onConfirm={performGhostLogin}
    />
  ), [ghostLoginConfirmOpen, selectedRow, performGhostLogin]);

  const UsersView = ({ viewType, columns, excludedFields }) => {
    const filteredColumns = useMemo(
      () => columns.filter((c) => !excludedFields.includes(c.field)),
      [columns, excludedFields]
    );
    return (
      <Collapse style={{ overflow: "auto" }} in={view === viewType || (viewType === "admin" && !view)} unmountOnExit>
        <Box mt={2} component={Paper} borderRadius={4}>
          <Box
            border="1px solid"
            borderColor="grey.400"
            borderRadius="10px 10px 0 0"
            p={1}
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography color="grey" fontWeight={600}>
              All {viewType}
            </Typography>
            <Tooltip title="Add new user">
              <IconButton
                size="small"
                onClick={() => {
                  setIsUpdate(false);
                  setUserBoxOpen(true);
                }}
              >
                <AddCircleIcon color="success" />
              </IconButton>
            </Tooltip>
          </Box>
          <Scrollbars style={{ height: 550, width: "100%" }}>
            <DataGrid
              rows={data}
              columns={filteredColumns}
              loading={loading}
              pageSizeOptions={[10, 20, 50]}
              paginationMode="server"
              rowHeight={38}
              rowCount={rowCount}
              paginationModel={{ ...paginationModel, page: paginationModel.page - 1 }}
              onPaginationModelChange={handlePaginationChange}
              sx={gridStyles}
              disableColumnSelector
              disableDensitySelector
              disableSelectionOnClick
              getRowClassName={(params) => (params.row.deleted ? "deleted-row" : "")}
            />
          </Scrollbars>
        </Box>
      </Collapse >
    );
  };
  const CategoryManagerDialogBox = useCallback(() => {
    return <CategoryManagerDialog open={categoriesBoxOpen} handleClose={() => {
      setcategoriesBoxOpen(false);
    }} />
  }, [categoriesBoxOpen])

  // Determine which fields to exclude based on view
  const excludedFields = useMemo(() => {
    if (view === "admin") return ["channels"];
    if (view === "creator") return ["channels", "promo_shares"];
    if (view === "promo") return ["creator_revenew"];
    return [];
  }, [view]);

  return (
    <Box>
      <Box
        mt={2}
        display="flex"
        flexDirection={{ xs: "column", md: "row" }}
        alignItems="center"
        gap={1}
        justifyContent="space-between"
      >
        <Typography variant="h4" sx={{ color: "grey.600" }}>
          Users List
        </Typography>
        <Box
          sx={{ bgcolor: "grey.300" }}
          p={{ xs: 1, md: 2 }}
          borderRadius={6.5}
        >
          <Button
            size="large"
            sx={{ color: "#000" }}
            startIcon={<CategoryIcon fontSize="large" color="success" />}
            onClick={() => { setcategoriesBoxOpen(true) }}
          >
            Categories
          </Button>
        </Box>
      </Box>

      <Box display="flex" justifyContent={{ xs: "space-between", md: "flex-start" }} gap={{ xs: 1, md: 2.5 }} mt={1}>
        {VIEW_TABS.map((tab) => {
          const selected = view === tab.value;
          return (
            <Button
              key={tab.value}
              onClick={() => {
                setPaginationModel((prev) => ({ ...prev, page: 1 }));
                setView(tab.value);
              }}
              sx={{
                borderRadius: 6,
                fontWeight: "bold",
                textTransform: "none",
                color: selected ? "white" : "grey",
                boxShadow:
                  selected &&
                  "0 5px 20px 0 rgba(0, 0, 0, 0.2), 0 13px 24px -11px rgb(102, 115, 252)",
                fontSize: { xs: "0.75rem", md: "1rem" },
                padding: { xs: "4px 8px", md: "8px 16px" },
              }}
              variant={selected ? "contained" : "text"}
            >
              {tab.label}
            </Button>
          );
        })}
      </Box>
      <UserFilters
        filters={filters}
        onChange={setFilters}
        resultCount={rowCount}
        loading={loading}
      />

      <UsersView viewType={view} columns={columns} excludedFields={excludedFields} />

      {/* Modals */}
      <CategoryManagerDialogBox />
      <UserDialog />
      <DeleteUserDialog />
      <PassResetDialog />
      <UserChannelsPreviewBox />
      <GhostLoginDialog />
    </Box>
  );
}

const mapStateToProps = (state) => ({
  user: state.user,
});

const mapDispatchToProps = (dispatch) => ({
  authSignup: (fields, afterSuccess) => dispatch(authSignup(fields, afterSuccess)),
  ghostLogin: (targetUserId, onSuccess, onError) => dispatch(ghostLogin(targetUserId, onSuccess, onError)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StaffList);
